import { useEffect, useRef, useState } from 'react';
import type { CodCheckoutDisplay } from '@xshop/shared';
import { bootData, fetchApi } from './api/client';
import type { PageType } from './themes/registry';
import { readStorefrontTheme, type StorefrontThemePage } from './themes/storefrontTheme';
import { previewThemeSlug } from './api/previewVisit';
import { requestedPage } from '@xshop/shared';
import { shopPage } from './themes/binding';
import { editorThemeSlug } from './editor/editorVisit';
import { isThemeSlug, type ThemeSlug } from './themeHost/themeMount';

/** What `GET /api/v1/tenant/theme` answers with: the active theme row and one resolved page. */
type ActiveThemeRow = { config?: unknown; theme?: { handle?: string | null } | null; storefront?: unknown };

/** The answer to one storefront theme read, once it has landed; null while it has not. */
type ThemeRead = { slug: ThemeSlug | null; config: StorefrontThemePage | null };

/** Which theme renders, which of its pages, and the config the server resolved for that one page. */
export type ActiveTheme = {
  slug: ThemeSlug | null;
  page: PageType;
  config: StorefrontThemePage | null;
  /**
   * Whether the read has come back at all. Without it, "still asking" and "asked, and this shop has no
   * theme" are the same empty answer — and a shop whose platform ships no themes yet sat on the loading
   * ground for ever, saying nothing, because the screen had no way to tell those two apart.
   */
  resolved: boolean;
};

// Spells the one storefront read: which page to resolve, and which theme when the visit names one of
// its own — a preview trying a theme on, or the builder editing one this store has installed.
const themeQuery = (page: PageType, handle: ThemeSlug | null, preview: boolean): string =>
  `/tenant/theme?page=${encodeURIComponent(page)}${handle ? `&theme=${encodeURIComponent(handle)}` : ''}${preview ? '&preview=1' : ''}`;

// Turns the API's answer into what the page draws from, keeping the theme only when this bundle can mount it.
function readOf(row: ActiveThemeRow | undefined, handle: ThemeSlug | null): ThemeRead {
  const resolved = handle ?? row?.theme?.handle;
  return { slug: isThemeSlug(resolved) ? resolved : null, config: readStorefrontTheme(row?.storefront) };
}

// Reads this store's theme and the single page it is showing — from the page's own answer when it carries one, so the
// first drawing (on the server too) is the store, not a loading ground.
function useStorefrontTheme(page: PageType, handle: ThemeSlug | null, preview: boolean): ThemeRead | null {
  const [read, setRead] = useState<ThemeRead | null>(() => {
    const row = bootData<ActiveThemeRow>(themeQuery(page, handle, preview));
    return row ? readOf(row, handle) : null;
  });
  // The page's own answer is the first read; asking again for it would only redraw the same page.
  const booted = useRef(read !== null);

  useEffect(() => {
    if (booted.current) { booted.current = false; return; }
    let cancelled = false;
    fetchApi<ActiveThemeRow>(themeQuery(page, handle, preview))
      .then((row) => {
        if (!cancelled) setRead(readOf(row, handle));
      })
      .catch((err: Error) => {
        // An answer, even a bad one. Leaving `read` null here spun the loading ground for ever on
        // every outage, which is the one state a shopper can do nothing with.
        console.warn('Active theme unavailable:', err.message);
        if (!cancelled) setRead({ slug: null, config: null });
      });
    return () => { cancelled = true; };
  }, [page, handle, preview]);

  return read;
}

// Says whether a page is one of the two that sell the product the address names.
const sellsAddressedProduct = (page: PageType): boolean => page === 'product' || page === 'product-cod';

// Resolves the theme to render, the page it is showing, and the config the server sent for that page.
export function useActiveTheme(display: CodCheckoutDisplay | null, productMissing = false): ActiveTheme {
  const [search, setSearch] = useState<string>(() => window.location.search);
  // The path too: an order confirmed at /thank-you leaves the query string as it was, so following it alone missed the move.
  const [path, setPath] = useState<string>(() => window.location.pathname);
  // The same read the API client makes, so the theme on screen and the shop behind it never disagree.
  const asked = previewThemeSlug(search);
  // The builder wins over a preview: it is drawing the merchant's own shop in a theme they installed,
  // where `?theme=` would swap the shop underneath it for the demo one a preview is judged against.
  const editing = editorThemeSlug(search);
  const handle = editing ?? (isThemeSlug(asked) ? asked : null);

  // The address bar is this storefront's only navigation, so back and forward have to re-read it.
  useEffect(() => {
    const onPopState = () => { setSearch(window.location.search); setPath(window.location.pathname); };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // The theme says what a page looks like; the shop's own settings say which pages it has. A visit that
  // named a theme is looking at the theme, so it keeps the theme's whole set instead of one store's
  // narrowing of it — a merchant cannot edit a page the builder has hidden from them.
  const requested = requestedPage(path, search);
  const narrowed = handle ? requested : shopPage(requested, display);
  // A product address the shop has no product for says so, rather than drawing a product page with nothing in it.
  const page = productMissing && sellsAddressedProduct(narrowed) ? 'not-found' : narrowed;
  const read = useStorefrontTheme(page, handle, !editing && asked !== null);

  return { slug: handle ?? read?.slug ?? null, page, config: read?.config ?? null, resolved: read !== null };
}
